import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageSpinner } from "@/components/ui/page-spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  STATUS_APOLICE_ROTULO,
  STATUS_COBRANCA_ROTULO,
  STATUS_COMISSAO_ROTULO,
  STATUS_PROPOSTA_ROTULO,
  chaveCobertura,
  rotuloSeguradora,
  type CoberturaCatalogoV,
} from "./seguradoras";
import { useTabelaSeguros } from "./useTabelaSeguros";

/**
 * Peças compartilhadas das telas de Seguros.
 *
 * A UI lê só as views `seguros_*_v`: o canônico (MAG, e o que vier) unido ao
 * canônico seguros_* (Azos, MAG e o que vier). Toda linha tem `seguradora`. RLS das tabelas
 * base vale pelas views (security_invoker), e é restrita a SUPER ADMIN
 * (migrations 98 e 158) — são dados de segurados das corretoras da rede.
 *
 * O vínculo corretor ↔ usuário nosso EXISTE no mundo, mas ainda não está
 * registrado (`seguros_corretores.user_id`, hoje nulo). Não inferir por nome:
 * foi medido, "MONTEIRO" casa 3 usuários com 2 corretoras. Errar aqui é
 * atribuir carteira alheia a alguém. Quando o user_id estiver preenchido, o
 * escopo passa a ser por ele.
 *
 * Consequência prática: quem não é super admin vê estas telas VAZIAS, não com
 * erro — a RLS filtra, não recusa.
 */

type Estilo = { label: string; classe: string };

// Cores de situação do design-system/north/MASTER.md: sempre com texto, nunca só cor.
const VERDE = "bg-ok-fundo text-ok";
const LARANJA = "bg-erro-fundo text-erro";
const VERMELHO = "bg-erro-fundo text-erro";
const AZUL = "bg-[#EEF1F6] text-primary";
const AMBAR = "bg-alerta-fundo text-alerta";
const NEUTRO = "bg-muted text-muted-foreground";

const comCor = (rotulos: Record<string, string>, cores: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(rotulos).map(([k, label]) => [k, { label, classe: cores[k] ?? NEUTRO }]),
  ) as Record<string, Estilo>;

export const STATUS_APOLICE: Record<string, Estilo> = comCor(STATUS_APOLICE_ROTULO, {
  vigente: VERDE,
  em_atraso: LARANJA,
  suspensa: AMBAR,
  cancelada: VERMELHO,
});

export const STATUS_PROPOSTA: Record<string, Estilo> = comCor(STATUS_PROPOSTA_ROTULO, {
  rascunho: NEUTRO,
  em_analise: AZUL,
  pendente: AMBAR,
  suspensa: AMBAR,
  aguardando_pagamento: AMBAR,
  aceita: VERDE,
  implantada: VERDE,
  recusada: VERMELHO,
  cancelada: VERMELHO,
  expirada: NEUTRO,
  desconhecido: NEUTRO,
});

export const STATUS_COBRANCA: Record<string, Estilo> = comCor(STATUS_COBRANCA_ROTULO, {
  pendente: AMBAR,
  em_atraso: LARANJA,
  recebida: AZUL,
  paga: VERDE,
  cancelada: VERMELHO,
});

export const STATUS_COMISSAO: Record<string, Estilo> = comCor(STATUS_COMISSAO_ROTULO, {
  pendente: AMBAR,
  paga: VERDE,
  estornada: VERMELHO,
});

export const StatusBadge = ({ status, mapa }: { status: string | null; mapa: Record<string, Estilo> }) => {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const cfg = mapa[status];
  return (
    <Badge
      className={`${cfg?.classe ?? NEUTRO} gap-1.5 rounded-[4px] border-0 px-2 py-0.5 text-[11.5px] font-semibold hover:bg-inherit before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-current before:content-['']`}
    >
      {cfg?.label ?? status}
    </Badge>
  );
};

/**
 * Badge curta da seguradora; aparece nas tabelas quando o filtro é "Todas".
 *
 * O NOME, na fonte do app (Rodrigo, 28/10/2026: com MAG, Azos, Omint e Icatu a
 * logo de cada uma vira uma coleção de estilos na mesma coluna). A logo fica
 * só nos chips do filtro (FiltroSeguradora) e na escolha da cotação.
 */
export const BadgeSeguradora = ({ codigo }: { codigo: string | null | undefined }) => (
  <Badge
    variant="outline"
    className="border-[rgba(53,70,96,0.25)] py-0.5 px-2 text-[11px] font-semibold uppercase tracking-wide"
  >
    {rotuloSeguradora(codigo)}
  </Badge>
);

/** Chips de filtro no padrão das telas (mesmo visual do FiltroSeguradora). */
export const Chips = <V extends string>({
  opcoes,
  valor,
  aoMudar,
}: {
  opcoes: { valor: V; rotulo: string }[];
  valor: V;
  aoMudar: (v: V) => void;
}) => (
  <>
    {opcoes.map((o) => (
      <button
        key={o.valor}
        type="button"
        data-slot="chip"
        onClick={() => aoMudar(o.valor)}
        aria-pressed={valor === o.valor}
        className={`h-7 cursor-pointer rounded-full border px-3 text-xs font-medium transition-colors ${
          valor === o.valor
            ? "border-marinho bg-marinho text-white"
            : "border-border bg-card text-foreground hover:bg-muted"
        }`}
      >
        {o.rotulo}
      </button>
    ))}
  </>
);

/**
 * O termo de busca casa com o documento?
 *
 * ARMADILHA que isto existe pra evitar: `termo.replace(/\D/g,'')` devolve string
 * VAZIA quando o usuário digita só letras — e `"qualquer".includes("")` é TRUE,
 * então toda linha casava e a busca por texto não filtrava nada. Só compara
 * documento quando o termo tem dígito.
 */
export const casaDocumento = (digitos: string | null | undefined, termo: string) => {
  const so = termo.replace(/\D/g, "");
  return so.length > 0 && !!digitos?.includes(so);
};

/** CPF só-dígitos vira 000.000.000-00; CNPJ vira 00.000.000/0000-00. */
export const formatarDocumento = (digitos: string | null) => {
  if (!digitos) return "—";
  if (digitos.length === 11)
    return digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (digitos.length === 14)
    return digitos.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return digitos;
};

export const PageHeader = ({
  titulo,
  descricao,
  icone,
  acoes,
}: {
  titulo: string;
  descricao: string;
  icone: ReactNode;
  acoes?: ReactNode;
}) => (
  // Premium North: o mesmo cabeçalho de CabecalhoPagina (bento/ui.tsx), com o ícone.
  <div className="cabecalho-premium relative flex flex-wrap items-center justify-between gap-3 overflow-hidden rounded-[14px] bg-premium px-5 py-5 text-white sm:px-6">
    <span
      aria-hidden
      className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full border border-ouro/35 shadow-[0_0_0_40px_rgba(196,173,142,0.05)]"
    />
    <div className="relative flex items-start gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-ouro [&_svg]:text-ouro">
        {icone}
      </span>
      <div>
        <h1 className="text-[24px] font-bold leading-tight tracking-[-0.02em] text-white sm:text-[26px]">{titulo}</h1>
        <p className="text-sm text-white/72">{descricao}</p>
      </div>
    </div>
    {acoes ? <div className="relative">{acoes}</div> : null}
  </div>
);

// A anatomia do card pequeno do bento — mesma do CartaoStat do kit; fica aqui
// só porque as telas de Seguros importam tudo deste comuns.
export const KpiCard = ({ rotulo, valor, detalhe }: { rotulo: string; valor: string; detalhe?: string }) => (
  <div className="cartao border-t-[3px] border-t-ouro bg-card px-4 py-3.5">
    <p className="truncate text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</p>
    <p className="mt-1.5 truncate text-[23px] font-bold leading-none tracking-[-0.02em] tabular-nums text-marinho">{valor}</p>
    {detalhe && <p className="mt-1.5 truncate text-[11.5px] text-[#6b7480] dark:text-muted-foreground">{detalhe}</p>}
  </div>
);

/**
 * Os indicadores numa faixa só (Premium North): um cartão com filete ouro e as
 * células divididas, no lugar de um cartão por número. Mesmos rótulos, valores
 * e detalhes do KpiCard.
 */
export const FaixaKpi = ({
  itens,
}: {
  itens: { rotulo: string; valor: ReactNode; detalhe?: ReactNode; tom?: "erro" | "ok" }[];
}) => (
  <div
    className={`cartao border-t-ouro grid overflow-hidden bg-card grid-cols-2 ${
      itens.length >= 4 ? "lg:grid-cols-4" : itens.length === 3 ? "sm:grid-cols-3" : ""
    } ${itens.length === 5 ? "xl:grid-cols-5" : ""}`}
  >
    {itens.map((k) => (
      <div key={k.rotulo} className="min-w-0 border-b border-l border-border px-4 py-3.5 [margin:0_0_-1px_-1px]">
        <p className="truncate text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{k.rotulo}</p>
        <p
          className={`mt-1.5 truncate text-[22px] font-bold leading-none tracking-[-0.02em] tabular-nums ${
            k.tom === "erro" ? "text-erro" : k.tom === "ok" ? "text-ok" : "text-marinho"
          }`}
        >
          {k.valor}
        </p>
        {k.detalhe ? <p className="mt-1.5 truncate text-[11.5px] text-muted-foreground">{k.detalhe}</p> : null}
      </div>
    ))}
  </div>
);

/** Cartão da tabela: a barra de busca e filtros mora dentro, em cima das linhas. */
export const CartaoTabela = ({ barra, children }: { barra?: ReactNode; children: ReactNode }) => (
  <Card className="gap-0 overflow-hidden py-0">
    {barra ? (
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">{barra}</div>
    ) : null}
    {children}
  </Card>
);

/**
 * Célula que quebra linha. A tabela nunca rola de lado (pedido do Rick,
 * 19/10/2026): dados que andam juntos dividem a célula em duas linhas.
 */
export const celula = "whitespace-normal align-top py-2.5";
/** Segunda linha de uma célula empilhada. */
export const linha2 = "mt-0.5 text-xs text-muted-foreground";

export const CampoBusca = ({
  valor,
  aoMudar,
  placeholder,
}: {
  valor: string;
  aoMudar: (v: string) => void;
  placeholder: string;
}) => (
  <Input
    value={valor}
    onChange={(e) => aoMudar(e.target.value)}
    placeholder={placeholder}
    className="sm:w-80"
  />
);

export const TabelaVazia = ({ colunas, mensagem }: { colunas: number; mensagem: string }) => (
  <TableRow>
    <TableCell colSpan={colunas} className="py-10 text-center text-sm text-muted-foreground">
      {mensagem}
    </TableCell>
  </TableRow>
);

export const ErroCarga = ({ oQue, erro }: { oQue: string; erro: string }) => (
  <Card>
    <CardContent className="pt-6 text-sm text-destructive">
      Não foi possível carregar {oQue}: {erro}
    </CardContent>
  </Card>
);

export { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Card, CardContent, PageSpinner };

/**
 * Catálogo de coberturas, indexado por `seguradora:codigo`.
 *
 * O mesmo código pode significar coisas diferentes em seguradoras diferentes,
 * por isso a chave leva as duas partes. Na Azos, seis códigos centrais (2003,
 * 2005, 2008...) não estão no catálogo publicado; até o catálogo canônico
 * aprender com os eventos (origem `evento`), a UI mostra o código e diz que é
 * desconhecido — não inventa rótulo nem esconde a linha.
 */
export const useCatalogoCoberturas = () => {
  const { linhas } = useTabelaSeguros<CoberturaCatalogoV>("seguros_coberturas_catalogo_v", {
    select: "seguradora, codigo, nome, tipo",
    limite: 5000,
  });
  const [mapa, setMapa] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setMapa(
      new Map(
        linhas.map((c) => [chaveCobertura(c.seguradora, c.codigo), c.nome ?? c.tipo ?? c.codigo]),
      ),
    );
  }, [linhas]);

  return mapa;
};

/**
 * O rótulo de uma cobertura — ou um aviso honesto quando não conhecemos o código.
 *
 * O caminho errado seria cair num "—" ou repetir o código cru: os dois fazem a
 * cobertura desconhecida parecer irrelevante, quando ela pode ser a principal da
 * apólice. Quem vê precisa saber que falta informação e o que fazer com isso.
 */
export const RotuloCobertura = ({
  seguradora,
  codigo,
  nome,
  catalogo,
}: {
  seguradora: string;
  codigo: string;
  /** Nome que veio junto com a apólice (MAG manda); ganha do catálogo. */
  nome?: string | null;
  catalogo: Map<string, string>;
}) => {
  const rotulo = nome ?? catalogo.get(chaveCobertura(seguradora, codigo));
  if (rotulo) return <span>{rotulo}</span>;

  const quem = rotuloSeguradora(seguradora);
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={`Este código não está no catálogo da ${quem}. Confirmar o significado antes de usar em proposta ou relatório.`}
    >
      <span>Cobertura {codigo}</span>
      <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-medium text-alerta">
        desconhecida · confirmar com a {quem}
      </span>
    </span>
  );
};
